import { createSlice } from '@reduxjs/toolkit';

import { STORAGE_KEYS } from '@/constants';

import type { PlantInfo, CarrerInfo } from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';

// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────

export interface User {
  _id: string;
  mobile_no: string;
  country_code: string;
  first_name: string;
  last_name: string;
  email?: string | undefined;
  profile_image?: string | undefined;
  role: string;
  plant_id?: number | undefined;
  plant_name?: string | undefined;
  mobile_verified?: boolean | undefined;
  carrier?: CarrerInfo[] | undefined;
  plants?: PlantInfo[] | undefined;
  preferred_language?: string | undefined;
  login_date_time?: string | undefined;
}

export interface Plant {
  _id: string;
  org_id: number;
  plant_code: string;
  plant_name: string;
  address: string;
  org_tz: string;
  org_city: string;
  org_state_prov: string;
  org_postal_code: string;
  task_acceptance_criteria: number;
  move_task_acceptance_criteria: number;
  audit_task_acceptance_criteria: number;
  is_enabled: number;
}

export interface UserState {
  // User data
  user: User | null;
  plant: Plant | null;
  sessionId: string | null;
  accessToken: string | null;
  tokenExpiry: number | null;

  // OTP flow state
  mobileNumber: string;
  countryCode: string;
  registered: boolean | null;

  // UI state
  isLoading: boolean;
  error: string | null;
}

// ─────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────

// TODO: SECURITY — VULN-02: Tokens should ultimately be stored in httpOnly,
// Secure, SameSite=Strict cookies set by the server.
// Auth session keys (user, accessToken, sessionId) persist in localStorage
// so the PWA can resume after app kill — matches Xamarin device storage behavior.

/** Keys that hold ephemeral data — stored in sessionStorage (clears on tab close). */
const SENSITIVE_KEYS = new Set<string>([
  STORAGE_KEYS.refreshToken,
  STORAGE_KEYS.otpMobile,
  STORAGE_KEYS.otpCountryCode,
]);

/**
 * Get the appropriate storage backend for a key.
 * Sensitive data uses sessionStorage; non-sensitive uses localStorage.
 */
function getStorageForKey(key: string): Storage {
  return SENSITIVE_KEYS.has(key) ? sessionStorage : localStorage;
}

/**
 * Persist a single auth key to the appropriate storage (fire-and-forget).
 */
function persistToStorage(key: string, value: unknown): void {
  try {
    const storage = getStorageForKey(key);
    if (value !== null && value !== undefined) {
      storage.setItem(key, JSON.stringify(value));
    } else {
      storage.removeItem(key);
    }
  } catch {
    // Silently fail if storage is unavailable
  }
}

/**
 * Read and parse a JSON value from the appropriate storage.
 * Returns null if missing, invalid, or storage is unavailable.
 */
function readFromStorage(key: string): unknown {
  try {
    const storage = getStorageForKey(key);
    const raw = storage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/**
 * One-time migration: move auth keys from sessionStorage to localStorage.
 * Previously these lived in sessionStorage (VULN-02 interim), which cleared
 * on app kill. Now they use localStorage to match Xamarin device-storage behavior.
 */
function migrateSessionToLocalStorage(): void {
  const keysToMigrate = [STORAGE_KEYS.authToken, STORAGE_KEYS.user, STORAGE_KEYS.sessionId];
  for (const key of keysToMigrate) {
    try {
      const value = sessionStorage.getItem(key);
      if (value !== null && localStorage.getItem(key) === null) {
        localStorage.setItem(key, value);
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore storage errors
    }
  }
}

function getAutoLogoutTimeFromConfig(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.globalConfig);
    if (!raw) return 0;
    const config = JSON.parse(raw) as { settings?: { APP_AUTO_LOGOUT_TIME?: string } };
    return parseFloat(config.settings?.APP_AUTO_LOGOUT_TIME ?? '0');
  } catch {
    return 0;
  }
}

function isColdStartInactivityExpired(): boolean {
  const autoLogoutSeconds = getAutoLogoutTimeFromConfig();
  if (autoLogoutSeconds <= 0) return false;

  try {
    const raw = localStorage.getItem(STORAGE_KEYS.lastActivityTime);
    if (!raw) return false;

    const lastActivity = JSON.parse(raw) as number;
    if (typeof lastActivity !== 'number' || isNaN(lastActivity)) return false;

    const elapsedSeconds = (Date.now() - lastActivity) / 1000;
    return elapsedSeconds >= autoLogoutSeconds;
  } catch {
    return false;
  }
}

function clearAllAuthStorage(): void {
  const localKeys = [
    STORAGE_KEYS.user,
    STORAGE_KEYS.sessionId,
    STORAGE_KEYS.authToken,
    STORAGE_KEYS.tokenExpiry,
    STORAGE_KEYS.plant,
    STORAGE_KEYS.breakModeActive,
    STORAGE_KEYS.breakModeFrom,
    STORAGE_KEYS.breakTimerStart,
    STORAGE_KEYS.shuttleModeActive,
    STORAGE_KEYS.shuttleTimerStart,
    STORAGE_KEYS.shuttleTimerPausedElapsed,
    STORAGE_KEYS.offlineUpdatedTaskList,
    STORAGE_KEYS.offlineMyTaskList,
    STORAGE_KEYS.inProgressTask,
    STORAGE_KEYS.offlineUpcomingTask,
    STORAGE_KEYS.offlineCompletedTask,
    STORAGE_KEYS.loginDateTime,
    STORAGE_KEYS.lastActivityTime,
    'yms_verify_data',
  ];
  for (const key of localKeys) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  const sessionKeys = [
    STORAGE_KEYS.refreshToken,
    STORAGE_KEYS.otpMobile,
    STORAGE_KEYS.otpCountryCode,
  ];
  for (const key of sessionKeys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Build initial state by hydrating from localStorage synchronously.
 * This ensures route guards see the correct auth state on the very first render.
 */
function loadInitialState(): UserState {
  migrateSessionToLocalStorage();

  const user = readFromStorage(STORAGE_KEYS.user) as User | null;
  const plant = readFromStorage(STORAGE_KEYS.plant) as Plant | null;
  const sessionId = readFromStorage(STORAGE_KEYS.sessionId) as string | null;
  const accessToken = readFromStorage(STORAGE_KEYS.authToken) as string | null;
  const tokenExpiry = readFromStorage(STORAGE_KEYS.tokenExpiry) as number | null;

  // Cold-start inactivity check: if the user was idle longer than
  // APP_AUTO_LOGOUT_TIME before the app was killed, clear auth so
  // ProtectedRoute redirects to /login — no API call needed.
  if (user && accessToken && isColdStartInactivityExpired()) {
    clearAllAuthStorage();
    return {
      user: null,
      plant: null,
      sessionId: null,
      accessToken: null,
      tokenExpiry: null,
      mobileNumber: '',
      countryCode: '+1',
      registered: null,
      isLoading: false,
      error: null,
    };
  }

  return {
    user,
    plant,
    sessionId,
    accessToken,
    tokenExpiry,
    mobileNumber: (readFromStorage(STORAGE_KEYS.otpMobile) as string | null) ?? '',
    countryCode: (readFromStorage(STORAGE_KEYS.otpCountryCode) as string | null) ?? '+1',
    registered: null,
    isLoading: false,
    error: null,
  };
}

const initialState: UserState = loadInitialState();

// ─────────────────────────────────────────────────────────────────
// Slice — client state only, synced from TanStack Query hooks
// ─────────────────────────────────────────────────────────────────

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Mobile number input — persisted for OTP page refresh
    setMobileNumber: (state, action: PayloadAction<string>) => {
      state.mobileNumber = action.payload;
      state.error = null;
      persistToStorage(STORAGE_KEYS.otpMobile, action.payload || null);
    },

    // Country code selection — persisted for OTP page refresh
    setCountryCode: (state, action: PayloadAction<string>) => {
      state.countryCode = action.payload;
      persistToStorage(STORAGE_KEYS.otpCountryCode, action.payload);
    },

    // Set user after OTP verification — persists to IndexedDB, clears OTP flow data
    // VULN-14: isAuthenticated requires both user AND accessToken
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      persistToStorage(STORAGE_KEYS.user, action.payload);
      // Clear OTP flow data from localStorage on successful login
      if (action.payload !== null) {
        persistToStorage(STORAGE_KEYS.otpMobile, null);
        persistToStorage(STORAGE_KEYS.otpCountryCode, null);
        // Matches Xamarin: _applicationContentService.User.LoginDateTime
        persistToStorage(
          STORAGE_KEYS.loginDateTime,
          action.payload.login_date_time ?? new Date().toISOString()
        );
        persistToStorage(STORAGE_KEYS.lastActivityTime, Date.now());
      }
    },

    // Set plant — persists to IndexedDB
    setPlant: (state, action: PayloadAction<Plant | null>) => {
      state.plant = action.payload;
      persistToStorage(STORAGE_KEYS.plant, action.payload);
    },

    // Set session ID — persists to IndexedDB
    setSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload;
      persistToStorage(STORAGE_KEYS.sessionId, action.payload);
    },

    // Set tokens after successful auth — persists to IndexedDB
    // VULN-14: isAuthenticated requires both user AND accessToken
    setTokens: (
      state,
      action: PayloadAction<{
        accessToken: string;
        expiresIn?: number;
      }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.tokenExpiry = action.payload.expiresIn ?? null;
      persistToStorage(STORAGE_KEYS.authToken, action.payload.accessToken);
      persistToStorage(STORAGE_KEYS.tokenExpiry, action.payload.expiresIn ?? null);
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Reset OTP flow (go back to mobile entry) — clears persisted OTP data
    resetOTPFlow: (state) => {
      state.mobileNumber = '';
      state.countryCode = '+1';
      state.registered = null;
      state.error = null;
      persistToStorage(STORAGE_KEYS.otpMobile, null);
      persistToStorage(STORAGE_KEYS.otpCountryCode, null);
    },

    clearAuth: (state) => {
      state.user = null;
      state.plant = null;
      state.sessionId = null;
      state.accessToken = null;
      state.tokenExpiry = null;
      state.mobileNumber = '';
      state.registered = null;
      state.error = null;
      persistToStorage(STORAGE_KEYS.user, null);
      persistToStorage(STORAGE_KEYS.plant, null);
      persistToStorage(STORAGE_KEYS.sessionId, null);
      persistToStorage(STORAGE_KEYS.authToken, null);
      persistToStorage(STORAGE_KEYS.tokenExpiry, null);
      persistToStorage(STORAGE_KEYS.lastActivityTime, null);
      // Clean up any leftover sessionStorage from old storage scheme
      try {
        sessionStorage.removeItem(STORAGE_KEYS.user);
        sessionStorage.removeItem(STORAGE_KEYS.sessionId);
        sessionStorage.removeItem(STORAGE_KEYS.authToken);
      } catch {
        // Ignore
      }
    },

    // Refresh token update — persists to IndexedDB
    refreshTokenUpdate: (
      state,
      action: PayloadAction<{ accessToken: string; expiresIn?: number }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.tokenExpiry = action.payload.expiresIn ?? null;
      persistToStorage(STORAGE_KEYS.authToken, action.payload.accessToken);
      persistToStorage(STORAGE_KEYS.tokenExpiry, action.payload.expiresIn ?? null);
    },
  },
});

export const {
  setMobileNumber,
  setCountryCode,
  setUser,
  setPlant,
  setSessionId,
  setTokens,
  setLoading,
  setError,
  resetOTPFlow,
  clearAuth,
  refreshTokenUpdate,
} = userSlice.actions;

// ─────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────

export const selectUser = (state: { user: UserState }) => state.user.user;
export const selectPlant = (state: { user: UserState }) => state.user.plant;
export const selectSessionId = (state: { user: UserState }) => state.user.sessionId;
export const selectAuthError = (state: { user: UserState }) => state.user.error;
export const selectregistered = (state: { user: UserState }) => state.user.registered;
export const selectMobileNumber = (state: { user: UserState }) => state.user.mobileNumber;
export const selectCountryCode = (state: { user: UserState }) => state.user.countryCode;
export const selectNeedsSignup = (state: { user: UserState }) =>
  state.user.user && !state.user.user.mobile_verified;
export const selectNeedsPlantSelection = (state: { user: UserState }) =>
  state.user.user && !state.user.plant;
export const selectAccessToken = (state: { user: UserState }) => state.user.accessToken;
export const selectTokenExpiry = (state: { user: UserState }) => state.user.tokenExpiry;
